/**
 * Is the caret currently inside an `@…` the user is still typing?
 *
 * Pure, because everything that makes mentions feel wrong lives here: opening the
 * popover on an email address, refusing to open it for someone with a two-word name,
 * or leaving it open forever after the query has clearly stopped being a name.
 *
 * Display names are `First Last` (formatProfileName), so the query cannot stop at the
 * first space -- it has to tolerate a couple. The limits below are what keep that from
 * turning every `@` in a paragraph into an open popover.
 */

/** "First Middle Last" is 2 spaces. A third means this stopped being a name. */
const MAX_SPACES = 2;
/** Longer than any display name we would offer, so a stray `@` gives up quickly. */
const MAX_QUERY_LENGTH = 32;

export type MentionQuery = {
  /** Index of the `@` itself. */
  start: number;
  /** Text between the `@` and the caret. Empty right after typing `@`. */
  query: string;
};

export function findMentionQuery(text: string, caret: number): MentionQuery | null {
  if (caret < 0 || caret > text.length) return null;

  // Nearest '@' at or before the caret.
  const start = text.lastIndexOf('@', caret - 1);
  if (start === -1) return null;

  // Must open a word: `me@example.com` is an address, not a mention.
  if (start > 0 && !/\s/.test(text[start - 1])) return null;

  const query = text.slice(start + 1, caret);
  if (query.length > MAX_QUERY_LENGTH) return null;
  if (query.includes('\n')) return null;

  // `@ ` is someone typing an at-sign, not reaching for a name.
  if (query.startsWith(' ')) return null;

  const spaces = query.split(' ').length - 1;
  if (spaces > MAX_SPACES) return null;

  return { start, query };
}
